import i18n from '../../i18n'
import { ApiClient } from './apiClient'
import { toCookieLocale } from '@/content/cookies/cookieInventory'
import type { RecordConsentPayload, StoredConsent, VerifyConsentPayload } from '@/types/consent'

/**
 * Talks to the consent audit log (POST /v2/consents and /v2/consents/verify). The
 * endpoints are public, so they work for visitors who have not signed in; if a session
 * token is present the ApiClient attaches it and the server links the row to the user.
 *
 * The log is evidence, not the enforcement point: callers apply the decision from the
 * browser immediately and never wait on these calls.
 */
class ConsentService extends ApiClient {
  async record(consent: StoredConsent): Promise<void> {
    const payload: RecordConsentPayload = {
      subjectId: consent.subjectId,
      policyVersion: consent.policyVersion,
      action: consent.action,
      categories: consent.categories,
      language: toCookieLocale(String(i18n.global.locale.value)),
    }
    await this.post('/consents', payload, { version: 'v2' })
  }

  /**
   * Does the log still hold this decision as the visitor's newest one? Only an explicit
   * `onFile: false` is a "no"; any other body counts as "yes", and a failed request
   * rejects. Callers treat both as "unknown, keep the decision", so a flaky network or an
   * API without this route never costs a visitor their choice.
   */
  async verify(consent: StoredConsent): Promise<boolean> {
    const payload: VerifyConsentPayload = {
      subjectId: consent.subjectId,
      policyVersion: consent.policyVersion,
      action: consent.action,
    }
    const response = await this.post<{ onFile?: unknown }>('/consents/verify', payload, {
      version: 'v2',
    })
    return response.data?.onFile !== false
  }
}

export const consentService = new ConsentService()
